import { Bundle } from '@spiffcommerce/core'
import cn from 'classnames'
import { Dispatch, useEffect, useState, useRef } from 'react'
import { useHeaderOffset } from '../../../hooks/useHeaderOffset'
import { formatMoney } from '../../../helpers/formatCentsToDollars'
import { CloseIcon } from '../../../icons/CloseIcon'
import { HamburgerIcon } from '../../../icons/HamburgerIcon'
import { CustomScene } from '../../../types/types'
import { Button } from '../../Button/Button'
import { Summary } from '../Summary/Summary'
import { useAppContext } from '../../../context/AppContext'
import { ClearSelectionsPopup } from '../ClearSelectionsPopup/ClearSelectionsPopup'

export const SceneMenu = ({
  bundle,
  previewService,
  allScenes,
  currentScene,
  showSummaryScene,
  canvasRef,
  sceneMenuIsOpen,
  setSceneMenuIsOpen,
  showClearSelectionsPopup,
  setShowClearSelectionsPopup,
  clearSelectionLinkPath,
  setCurrentScene,
  setShowSummaryScene,
  handleAddToCart,
  clearSelections,
  addedToCart
}: {
  bundle: Bundle
  previewService: any
  allScenes: CustomScene[]
  currentScene: number
  showSummaryScene: boolean
  canvasRef: React.RefObject<HTMLCanvasElement>
  sceneMenuIsOpen: boolean
  setSceneMenuIsOpen: Dispatch<React.SetStateAction<boolean>>
  showClearSelectionsPopup: boolean
  setShowClearSelectionsPopup: Dispatch<React.SetStateAction<boolean>>
  clearSelectionLinkPath: string
  setCurrentScene: Dispatch<React.SetStateAction<number>>
  setShowSummaryScene: Dispatch<React.SetStateAction<boolean>>
  handleAddToCart: () => void
  clearSelections: () => Promise<void>
  addedToCart: boolean
}) => {
  const { isOnBodyPreview } = useAppContext()
  const numberOfPendantScenes = allScenes.filter((scene) => scene?.isPendantScene).length
  const selectedScene = allScenes[currentScene]
  const isPendantScene = selectedScene?.isPendantScene
  const pendantSceneLabel = `Pendant#${selectedScene?.workflowPosition}`
  const isFirstScene = currentScene === 0
  const isLastScene = currentScene === allScenes.length - 1
  const [currentCanvasOffset, setCurrentCanvasOffset] = useState(0)
  const headerOffset = useHeaderOffset()
  const builderTitleBar = document.getElementById('builder-title-bar')
  const announcementBar = document.getElementById('shopify-section-announcement')
  const announcementBarHeight = announcementBar?.offsetHeight || 0
  const [previewData, setPreviewData] = useState<any>(null)
  const lengthScene = allScenes.find((scene) => scene?.name.toLowerCase().includes('length'))
  const lengthSceneSteps = lengthScene?.stepIds.map((stepId) => bundle.getWorkflowExperiences()[lengthScene.workflowPosition].getStepById(stepId))
  const summaryRef = useRef<HTMLDivElement>(null)

  const getPreviewServiceAsset = async () => {
    const baseWorkflowExperienceManager = bundle.getWorkflowExperiences()[0].getWorkflowManager()
    const cameraOrientation = baseWorkflowExperienceManager.getWorkflow()?.finalizeStepConfig?.lookAtAnimation
    const previewData = await baseWorkflowExperienceManager.getPreviewService()?.renderSceneScreenshot(512, cameraOrientation!)

    setPreviewData(previewData)
  }

  const toggleBodyLock = () => {
    if (!sceneMenuIsOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.removeAttribute('style')
    }
  }

  const scrollToTop = () => {
    if (window.innerWidth < 768) {
      window.scrollTo(0, announcementBarHeight)
    }
  }

  const getSceneLabel = () => {
    if (showSummaryScene) return 'Summary'
    if (isPendantScene) return pendantSceneLabel
    return selectedScene?.name
  }

  const getNextButtonLabel = () => {
    if (isLastScene) return 'Add to Bag'
    return 'Next'
  }

  const detectHeightChange = () => {
    const canvas = canvasRef?.current
    if (!canvas) return

    setCurrentCanvasOffset(canvas.offsetHeight)
  }

  useEffect(() => {
    detectHeightChange()
    window.addEventListener('resize', detectHeightChange)

    return () => window.removeEventListener('resize', detectHeightChange)
  }, [])

  useEffect(() => {
    if (showClearSelectionsPopup) {
      getPreviewServiceAsset()
    }
  }, [showClearSelectionsPopup])

  return (
    <>
      <div
        className="sticky z-[2] md:static bg-background-light flex items-stretch justify-between w-full border-ink/10 border-y"
        style={{
          top: `${currentCanvasOffset + 40 + (builderTitleBar?.offsetHeight || 0)}px`
        }}
      >
        <div className="flex items-center md-max:w-1/2 flex-1 px-[15px] md:px-5 min-h-[35px] md:min-h-[44px] border-0">
          <Button
            disabled={addedToCart}
            onClick={() => {
              if (addedToCart) return
              setSceneMenuIsOpen(true)
              toggleBodyLock() // only lock the body on mobile
            }}
            className="flex items-center gap-2.5"
          >
            <HamburgerIcon />
            <p className="uppercase Button bg-background-light px-0 text-left text-sm-b w-[calc(100%_-_32px)] flex flex-wrap">
              {getSceneLabel()}
              <span className="text-deep-grey ml-1.5">({currentScene + 1}/{allScenes.length})</span>
            </p>
          </Button>
        </div>

        <div className={cn('flex w-1/2')}>
          {!isFirstScene && (
            <Button
              onClick={() => {
                scrollToTop()
                if (isFirstScene) return
                if (showSummaryScene) {
                  setShowSummaryScene(false)
                  lengthSceneSteps?.forEach((step) => step?.executeAnimations())
                  return
                }
                setCurrentScene(currentScene - 1)
              }}
              className="Button md-max:w-1/2 lg:min-w-[123px] min-h-[35px] md:min-h-[44px] rounded-none hover:bg-ink hover:text-white border-ink/10 border-l text-sm-b"
            >
              {isFirstScene ? '' : 'Back'}
            </Button>
          )}
          <Button
            onClick={() => {
              scrollToTop()
              if (isLastScene) return handleAddToCart()
              setCurrentScene(currentScene + 1)
            }}
            disabled={addedToCart && isLastScene}
            className={cn(
              'Button md-max:w-1/2 lg:min-w-[123px] min-h-[35px] md:min-h-[44px] rounded-none hover:bg-ink hover:text-white border-ink/10 border-l text-sm-b',
              isLastScene ? 'bg-ink text-white rounded-none min-w-[105px] lg:min-w-[123px]' : '',
              isFirstScene ? 'w-full' : ''
            )}
          >
            {getNextButtonLabel()}
          </Button>
        </div>
      </div>

      <div
        className={cn(
          'fixed top-0 w-full h-full flex flex-col bg-white z-50 transition-all duration-300 ease-linear overflow-x-hidden overflow-y-scroll md:w-[598px]',
          sceneMenuIsOpen ? 'right-0' : '-right-full md:-right-[598px]'
        )}
        ref={summaryRef}
      >
        <div className="sticky top-0 bg-white w-full flex items-center justify-between px-5 py-[17px]">
          <div className="relative flex items-center justify-center gap-4 flex-1 border-0">
            <button
              onClick={async () => {
                setSceneMenuIsOpen(false)
                toggleBodyLock() // only lock the body on mobile
                setTimeout(() => {
                  setShowClearSelectionsPopup(false)
                }, 300)
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2"
            >
              <CloseIcon />
            </button>
            <p className="uppercase text-sm">Selections</p>
          </div>
        </div>

        <div className="flex-1 px-5 pb-10 md:pb-[60px] md:px-[60px]">
          <div className="flex flex-col text-center mb-[30px] mt-[35px] md:mb-[45px] md:mt-[69px]">
            <h4 className="text-xl uppercase font-light mb-[15px] tracking-normal md:mb-5 md:text-2xl">Summary</h4>
            <button
              onClick={async () => {
                await getPreviewServiceAsset()
                setShowClearSelectionsPopup(true)
              }}
              className="flex items-center justify-center uppercase underline underline-offset-4 font-calibre text-sm-b hover:text-subdued"
            >
              Clear Selections
            </button>
          </div>

          <Summary
            isSceneMenu={true}
            allScenes={allScenes}
            numberOfPendantScenes={numberOfPendantScenes}
            currentScene={currentScene}
            onClick={(i) => {
              setCurrentScene(i)
              setSceneMenuIsOpen(false)
              toggleBodyLock()
              if (showSummaryScene) setShowSummaryScene(false)
            }}
            className=""
          />

          <div className="flex justify-between uppercase py-[15px] border-t border-ink">
            <span className="text-sm-c font-calibre text-black">SubTotal</span>
            <span className="text-xs font-calibre text-black">{formatMoney(bundle.getTotalSubunits())}</span>
          </div>
        </div>

        <div className={`absolute top-0 w-full h-fit bg-white z-10 transition-all duration-300 ease-linear pb-10 md:pb-[60px] ${showClearSelectionsPopup ? 'right-0' : '-right-full'}`}>
          <ClearSelectionsPopup
            previewData={previewData}
            clearSelectionLinkPath={clearSelectionLinkPath}
            setShowClearSelectionsPopup={setShowClearSelectionsPopup}
            onClick={async () => {
              await clearSelections()
              setCurrentScene(2) // Restart from Length scene
              setSceneMenuIsOpen(false)
              toggleBodyLock()
              setShowClearSelectionsPopup(false)
              setShowSummaryScene(false)
            }}
          />
        </div>
      </div>
      <div
        className={`PageOverlay ${sceneMenuIsOpen ? 'is-visible' : ''}`}
        onClick={async () => {
          setSceneMenuIsOpen(false)
          toggleBodyLock() // only lock the body on mobile
          setTimeout(() => {
            setShowClearSelectionsPopup(false)
          }, 300)
        }}
      ></div>
    </>
  )
}
